import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Zap, Play, Pause, SkipForward,
  Volume2, VolumeX, Clock, Check, Circle,
  ChevronDown, RotateCcw,
  Users, Sparkles, ArrowRight, Globe,
  CheckSquare, Lightbulb
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SmartCard from "@/components/wingman/SmartCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, BACKEND_URL } from "@/App";
import posthog from "@/lib/posthog";

const PHASES = ["meeting"];
const DEMO_USER = "You";
const DEMO_TRANSCRIPT = [
  { id: "t1", speaker: "Jordan (Client)", text: "I think the European market is about 40% of global SaaS spend, right?", time: 0, duration: 4730 },
  { id: "t2", speaker: "You", text: "It's actually around 32%. North America leads at 52%. But Europe is still the second largest, strong opportunity.", time: 6500, duration: 5500 },
  { id: "t3", speaker: "Jordan (Client)", text: "Makes sense. I think the implementation will take about 6 months based on similar projects we've done.", time: 15500, duration: 6000 },
  { id: "t4", speaker: "You", text: "We can do much better. Our last three rollouts averaged 11 weeks. Shortest was 8 weeks for a similar-sized client.", time: 23500, duration: 6000 },
  { id: "t5", speaker: "Jordan (Client)", text: "That's encouraging. What about the budget side of things?", time: 33000, duration: 3290 },
  { id: "t6", speaker: "You", text: "From our proposal, we're looking at $45 per seat for 500 users, with volume pricing as you scale.", time: 38000, duration: 5500 },
  { id: "t7", speaker: "Jordan (Client)", text: "This is looking good. Let me align with my team and circle back next Wednesday.", time: 47000, duration: 4680 },
];

const CARD_DELAY_MS = 50;

const segmentEndById = Object.fromEntries(DEMO_TRANSCRIPT.map((s) => [s.id, s.time + s.duration]));

const DEMO_CARDS = [
  {
    card_id: "demo_fact_europe",
    card_type: "fact_check",
    triggerSegment: "t1",
    urgency: "immediate",
    agent: "fact_surfacer",
    content: {
      claim: "European market is about 40% of global SaaS spend",
      verdict: "partially_accurate",
      explanation: "32.4% per Statista 2025. North America leads at 52%. Europe is the second-largest but not 40%.",
      sources: ["https://www.statista.com/statistics/global-saas-market"],
      grounded: false,
    },
  },
  {
    card_id: "demo_grounded_proposal",
    card_type: "grounded",
    triggerSegment: "t5",
    urgency: "immediate",
    agent: "grounded_agent",
    content: {
      suggestion: "From your proposal: Year 1 target was 500 users at $45/seat, with 20% annual growth.",
      evidence: "Q4 Proposal, Section 3: Pricing & Scale",
      trigger: "Jordan asked about budget, surfacing proposal pricing",
      source_title: "Q4 Proposal.pdf",
    },
  },
  {
    card_id: "demo_fact_impl",
    card_type: "fact_check",
    triggerSegment: "t3",
    urgency: "immediate",
    agent: "fact_surfacer",
    content: {
      claim: "Implementation will take about 6 months",
      verdict: "inaccurate",
      explanation: "Your last 3 implementations averaged 11 weeks. Shortest was 8 weeks for a similar-sized client.",
      sources: [],
      grounded: true,
    },
  },
  {
    card_id: "demo_nudge_pricing",
    card_type: "nudge",
    triggerSegment: "t4",
    urgency: "immediate",
    agent: "agenda_tracker",
    content: {
      transition: "You haven't discussed pricing or budget yet. 6 minutes remaining.",
      uncovered_item: "Pricing & Budget",
      reason: "Pricing is still uncovered. Good time to bring it up before the call wraps.",
    },
  },
  {
    card_id: "demo_action",
    card_type: "action_item",
    triggerSegment: "t7",
    urgency: "when_ready",
    agent: "action_tracker",
    content: {
      action: "Jordan to align with team and circle back",
      owner: "Jordan",
      deadline: "next Wednesday",
      quote: "Jordan to align with team and circle back next Wednesday.",
    },
  },
].map((c) => ({
  ...c,
  time: segmentEndById[c.triggerSegment] + CARD_DELAY_MS,
  timestamp: new Date(Date.now() + segmentEndById[c.triggerSegment] + CARD_DELAY_MS).toISOString(),
}));

const DEMO_TOTAL_DURATION = 55000;

const DEMO_AGENDA = [
  { item_id: "demo_ag1", title: "Market & Scope", time_alloc_mins: 5 },
  { item_id: "demo_ag2", title: "Implementation Plan", time_alloc_mins: 8 },
  { item_id: "demo_ag3", title: "Pricing & Next Steps", time_alloc_mins: 5 },
];
const DEMO_COVERAGE_AT_MS = {
  demo_ag1: segmentEndById.t2 ?? 14000,
  demo_ag2: segmentEndById.t4 ?? 33000,
  demo_ag3: segmentEndById.t7 ?? 56000,
};
const DEMO_AGENDA_NOTES = {
  demo_ag1: ["Europe is 32.4% of global SaaS (not 40%)", "Proposal targets: 500 users, $45/seat, 20% growth"],
  demo_ag2: ["Avg implementation: 11 weeks", "Shortest rollout: 8 weeks for similar-sized client"],
  demo_ag3: ["$45/seat for 500 users, volume pricing at scale", "Jordan to align with team by Wednesday"],
};

function speakText(text, voiceEnabled) {
  if (!voiceEnabled || typeof window === "undefined" || !window.speechSynthesis) return;
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 1.05;
  utterance.pitch = 1;
  window.speechSynthesis.speak(utterance);
}

function playSegmentAudio(segId, text, voiceEnabled, audioRef) {
  if (typeof window === "undefined") return;
  const audio = new Audio(`/demo-audio/${segId}.mp3`);
  audio.volume = voiceEnabled ? 1 : 0;
  audioRef.current = audio;
  audio.play().catch(() => {
    if (voiceEnabled) speakText(text, true);
  });
  audio.onended = () => { audioRef.current = null; };
  audio.onerror = () => { if (voiceEnabled) speakText(text, true); };
}

export default function DemoMeeting() {
  const navigate = useNavigate();
  const authContext = useAuth();
  const user = authContext?.user;
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [cards, setCards] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceover, setVoiceover] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [meetingComplete, setMeetingComplete] = useState(false);
  const [coverage, setCoverage] = useState({});
  const [introStep, setIntroStep] = useState(0); // 0=scene, 1=expect, 2=how, 3=countdown, -1=dismissed
  const [countdown, setCountdown] = useState(3);
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [shownTooltips, setShownTooltips] = useState({});
  const [mobileAgendaOpen, setMobileAgendaOpen] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const lastSpokenRef = useRef(null);
  const currentAudioRef = useRef(null);
  const countdownRef = useRef(null);
  const isPlayingRef = useRef(false);
  const voiceoverRef = useRef(voiceover);
  const shownTooltipsRef = useRef(shownTooltips);

  voiceoverRef.current = voiceover;
  shownTooltipsRef.current = shownTooltips;

  const phase = PHASES[phaseIndex];
  const showIntroModal = introStep >= 0;

  useEffect(() => {
    localStorage.setItem("shadow_demo_visited", "true");
  }, []);

  const tick = useCallback(() => {
    if (!startTimeRef.current || !isPlayingRef.current) return;
    const now = Date.now() - startTimeRef.current;
    setElapsed(now);

    DEMO_TRANSCRIPT.forEach((seg) => {
      if (now >= seg.time) {
        setTranscript((prev) => {
          if (prev.find((p) => p.id === seg.id)) return prev;
          if (lastSpokenRef.current !== seg.id && isPlayingRef.current) {
            lastSpokenRef.current = seg.id;
            playSegmentAudio(seg.id, seg.text, voiceoverRef.current, currentAudioRef);
          }
          return [...prev, seg];
        });
      }
    });

    DEMO_CARDS.forEach((card) => {
      if (now >= card.time) {
        setCards((prev) => {
          if (prev.find((p) => p.card_id === card.card_id)) return prev;
          posthog.capture("demo_card_appeared", { card_type: card.card_type, card_id: card.card_id });
          return [{ ...card, pinned: false, used: false }, ...prev];
        });
      }
    });

    setCoverage((prev) => {
      let changed = false;
      const next = { ...prev };
      DEMO_AGENDA.forEach((item) => {
        const coveredAt = DEMO_COVERAGE_AT_MS[item.item_id];
        if (coveredAt != null && now >= coveredAt && !next[item.item_id]?.covered) {
          next[item.item_id] = {
            ...(next[item.item_id] || {}),
            covered: true,
            notes: DEMO_AGENDA_NOTES[item.item_id] || [],
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    if (now >= DEMO_TOTAL_DURATION) {
      stopAllAudio();
      isPlayingRef.current = false;
      setMeetingComplete(true);
      clearInterval(timerRef.current);
      timerRef.current = null;
      posthog.capture("demo_completed");
    }
  }, []);

  const startMeeting = () => {
    isPlayingRef.current = true;
    setIsPlaying(true);
    startTimeRef.current = Date.now() - elapsed;

    if (currentAudioRef.current && currentAudioRef.current.paused) {
      currentAudioRef.current.play().catch(() => {});
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.resume();
    }

    timerRef.current = setInterval(tick, 200);
  };

  const pauseMeeting = () => {
    isPlayingRef.current = false;
    clearInterval(timerRef.current);
    timerRef.current = null;
    setIsPlaying(false);
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.pause();
    }
  };

  const stopAllAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  const muteAudio = () => {
    if (currentAudioRef.current) currentAudioRef.current.volume = 0;
  };

  const unmuteAudio = () => {
    if (currentAudioRef.current) currentAudioRef.current.volume = 1;
  };

  const skipMeetingToEnd = () => {
    const segs = DEMO_TRANSCRIPT;
    const cardList = DEMO_CARDS;
    setTranscript(segs);
    setCards(cardList.map((c) => ({ ...c, pinned: false, used: false })));
    setCoverage(
      Object.fromEntries(
        DEMO_AGENDA.map((i) => [
          i.item_id,
          { covered: true, notes: DEMO_AGENDA_NOTES[i.item_id] || [] },
        ])
      )
    );
    isPlayingRef.current = false;
    setMeetingComplete(true);
    setIsPlaying(false);
    clearInterval(timerRef.current);
    timerRef.current = null;
    stopAllAudio();
    posthog.capture("demo_completed");
  };

  const handleStartDemo = () => {
    posthog.capture("demo_started");
    setIntroStep(3);
    setCountdown(3);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setIntroStep(-1);
          startMeeting();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGoogleLogin = () => {
    posthog.capture("demo_signup_clicked");
    window.location.href = `${BACKEND_URL}/api/auth/google`;
  };

  const replayDemo = () => {
    setElapsed(0);
    setTranscript([]);
    setCards([]);
    setCoverage({});
    setMeetingComplete(false);
    setShownTooltips({});
    startTimeRef.current = null;
    lastSpokenRef.current = null;
    setIntroStep(0);
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (transcriptOpen) {
      transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript, transcriptOpen]);



  const dismissCard = (cardId) => setCards((prev) => prev.filter((c) => c.card_id !== cardId));
  const pinCard = (cardId) => setCards((prev) => prev.map((c) => c.card_id === cardId ? { ...c, pinned: !c.pinned } : c));
  const markUsed = (cardId) => setCards((prev) => prev.map((c) => c.card_id === cardId ? { ...c, used: true } : c));

  const latestTranscriptLine = transcript.length > 0 ? transcript[transcript.length - 1] : null;

  const cardMarkers = DEMO_CARDS.map((c) => ({
    position: (c.time / DEMO_TOTAL_DURATION) * 100,
    type: c.card_type,
    appeared: elapsed >= c.time,
  }));

  const currentItemIdx = DEMO_AGENDA.findIndex((item) => !coverage[item.item_id]?.covered);
  const coveredCount = DEMO_AGENDA.filter((item) => coverage[item.item_id]?.covered).length;

  const totalDuration = DEMO_TOTAL_DURATION;
  const progress = Math.min((elapsed / totalDuration) * 100, 100);
  const displayedDemoCards = [...cards].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.time || 0) - (a.time || 0);
  });

  return (
    <div className="h-screen mesh-bg flex flex-col overflow-hidden">
      {/* Multi-step intro briefing */}
      <AnimatePresence>
        {showIntroModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          >
            <motion.div
              key={introStep}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full shadow-xl border border-gray-100"
            >
              {introStep === 0 && (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-6">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2 text-center">
                    Strategy Call
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6 text-center">
                    You're about to join a strategy call with a client exploring expansion.
                  </p>
                  <div className="bg-secondary/50 rounded-xl p-4 mb-6 space-y-3">
                    {[
                      { name: "You", role: "Your side", highlight: true },
                      { name: "Jordan", role: "Client" },
                    ].map((p) => (
                      <div key={p.name} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${p.highlight ? "bg-primary text-primary-foreground" : "bg-gray-200 text-gray-600"}`}>
                          {p.name[0]}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-foreground">{p.name}</span>
                          {p.highlight && <span className="text-xs text-primary ml-2">(You)</span>}
                        </div>
                        <span className="text-xs text-muted-foreground">{p.role}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setIntroStep(1)}
                    className="w-full bg-primary text-primary-foreground py-3 text-sm rounded-lg hover:bg-primary/90 transition-all font-medium flex items-center justify-center gap-2"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </>
              )}

              {introStep === 1 && (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2 text-center">
                    What to Expect
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6 text-center">
                    Shadow listens to the conversation and helps you in three ways.
                  </p>
                  <div className="space-y-3 mb-6">
                    {[
                      { icon: Globe, label: "Real-time facts", desc: "Catches wrong numbers, verifies claims from the web", color: "text-blue-500 bg-blue-50" },
                      { icon: Lightbulb, label: "Your docs at hand", desc: "Surfaces relevant info from your uploaded documents", color: "text-teal-500 bg-teal-50" },
                      { icon: CheckSquare, label: "Never miss what matters", desc: "Tracks your agenda and flags action items", color: "text-slate-600 bg-slate-100" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.color}`}>
                          <item.icon className="w-4.5 h-4.5" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-foreground">{item.label}</span>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setIntroStep(0)} className="flex-1 py-3 text-sm rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors font-medium">
                      Back
                    </button>
                    <button
                      onClick={() => setIntroStep(2)}
                      className="flex-1 bg-primary text-primary-foreground py-3 text-sm rounded-lg hover:bg-primary/90 transition-all font-medium flex items-center justify-center gap-2"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}

              {introStep === 2 && (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mx-auto mb-6">
                    <Zap className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2 text-center">
                    How It Works
                  </h3>
                  <div className="space-y-4 my-6">
                    {[
                      { step: "1", text: "Meeting starts, conversation plays automatically" },
                      { step: "2", text: "Shadow listens and analyzes every statement" },
                      { step: "3", text: "Smart Cards appear as nudges in real time" },
                    ].map((s) => (
                      <div key={s.step} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
                          {s.step}
                        </div>
                        <p className="text-sm text-foreground/80 pt-1">{s.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setIntroStep(1)} className="flex-1 py-3 text-sm rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors font-medium">
                      Back
                    </button>
                    <button
                      onClick={handleStartDemo}
                      className="flex-1 bg-primary text-primary-foreground py-3 text-sm rounded-lg hover:bg-primary/90 transition-all font-medium flex items-center justify-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      Start Meeting
                    </button>
                  </div>
                </>
              )}

              {introStep === 3 && (
                <div className="flex flex-col items-center justify-center py-8">
                  <motion.div
                    key={countdown}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6"
                  >
                    <span className="text-5xl font-bold text-primary">{countdown}</span>
                  </motion.div>
                  <p className="text-muted-foreground text-sm">Meeting starting...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="surface-nav sticky top-0 z-50 px-3 sm:px-6 py-2.5">
        <div className="relative flex items-center justify-between max-w-[1800px] mx-auto">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(user ? -1 : "/")}
              className="text-muted-foreground hover:text-foreground h-8 px-2 text-[13px]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="hidden sm:block">
              <h1 className="font-heading text-foreground text-lg leading-tight truncate max-w-[300px]">
                Demo: Strategy Call
              </h1>
              <p className="text-[13px] text-muted-foreground truncate max-w-[350px]">
                You + Jordan (Client)
              </p>
            </div>
          </div>

          {phase === "meeting" && !meetingComplete && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
              <Button
                size="sm"
                onClick={isPlaying ? pauseMeeting : startMeeting}
                className="h-8 text-[13px] rounded-lg px-3 sm:px-4"
              >
                {isPlaying ? <Pause className="w-4 h-4 sm:mr-1.5" /> : <Play className="w-4 h-4 sm:mr-1.5" />}
                <span className="hidden sm:inline">{isPlaying ? "Pause" : elapsed === 0 ? "Start" : "Resume"}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={skipMeetingToEnd}
                className="h-8 text-[13px] text-muted-foreground px-2 sm:px-3"
              >
                <SkipForward className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Skip</span>
              </Button>
            </div>
          )}

          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <button
              onClick={() => {
                setVoiceover((v) => {
                  const next = !v;
                  voiceoverRef.current = next;
                  if (next) {
                    unmuteAudio();
                  } else {
                    muteAudio();
                  }
                  return next;
                });
              }}
              className={`flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
                voiceover ? "bg-primary/5 text-primary" : "bg-secondary text-muted-foreground"
              }`}
              title={voiceover ? "Voiceover on" : "Voiceover off"}
            >
              {voiceover ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{voiceover ? "Voice" : "Muted"}</span>
            </button>

            <Badge variant="outline" className="gap-1.5 text-xs h-7">
              <span className={`w-1.5 h-1.5 rounded-full ${isPlaying ? "bg-emerald-500 animate-pulse" : meetingComplete ? "bg-muted-foreground/30" : "bg-amber-500"}`} />
              <span className="hidden sm:inline">{meetingComplete ? "Complete" : isPlaying ? "Connected" : "Paused"}</span>
            </Badge>
          </div>
        </div>
      </header>

      {/* Progress bar with card markers */}
      {phase === "meeting" && (
        <div className="h-1.5 bg-border/40 relative">
          <div
            className="h-full bg-primary transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
          {cardMarkers.map((m, i) => (
            <div
              key={i}
              className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white transition-colors ${
                m.appeared ? "bg-primary" : "bg-muted-foreground/30"
              }`}
              style={{ left: `${m.position}%` }}
              title={m.type}
            />
          ))}
        </div>
      )}

      <main className="flex-1 overflow-hidden relative">
        <div className="h-full px-3 sm:px-6 pt-3 sm:pt-5 pb-4 max-w-[1800px] mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 h-full">

            {/* Mobile agenda toggle */}
            <button
              onClick={() => setMobileAgendaOpen(!mobileAgendaOpen)}
              className="md:hidden flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-border"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Agenda</span>
                <span className="text-xs text-muted-foreground">{coveredCount}/{DEMO_AGENDA.length}</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${mobileAgendaOpen ? "rotate-180" : ""}`} />
            </button>

            {/* LEFT: Agenda Rail */}
            <div className={`${mobileAgendaOpen ? "block" : "hidden"} md:block w-full md:w-[320px] md:shrink-0 flex flex-col overflow-hidden min-h-0`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <h2 className="font-heading text-foreground text-base">Agenda</h2>
                </div>
                <div className="flex items-center gap-1.5">
                  {DEMO_AGENDA.map((item, idx) => (
                    <div
                      key={item.item_id}
                      className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                        coverage[item.item_id]?.covered
                          ? "bg-emerald-500"
                          : idx === currentItemIdx
                            ? "bg-primary animate-pulse"
                            : "bg-border"
                      }`}
                    />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1.5 font-mono tabular-nums">
                    {coveredCount}/{DEMO_AGENDA.length}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1">
                {DEMO_AGENDA.map((item) => {
                  const cov = coverage[item.item_id];
                  const isCovered = cov?.covered ?? false;
                  const notes = cov?.notes || [];

                  return (
                    <div key={item.item_id} className="px-3 py-2.5 rounded transition-colors">
                      <div className="flex items-center gap-3">
                        {isCovered ? (
                          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-border/40 flex items-center justify-center shrink-0">
                            <Circle className="w-3 h-3 text-muted-foreground/40" />
                          </div>
                        )}
                        <span className={`text-sm flex-1 truncate ${isCovered ? "text-muted-foreground" : "text-foreground/80"}`}>
                          {item.title}
                        </span>
                        <span className="text-xs text-muted-foreground/50 font-mono shrink-0">
                          {item.time_alloc_mins}m
                        </span>
                      </div>
                      {notes.length > 0 && (
                        <div className="ml-9 mt-2.5 pt-2 border-t border-border/30 space-y-1.5">
                          <span className="text-[11px] font-medium text-emerald-600/70 uppercase tracking-wide">Notes</span>
                          {notes.map((note, ni) => (
                            <div key={ni} className="flex items-start gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-[7px] shrink-0" />
                              <p className="text-[13px] text-foreground/60 leading-relaxed">{note}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Transcript + Cards */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden gap-3">

              {/* Transcript accordion */}
              <div className="shrink-0 rounded-lg border border-border bg-white overflow-hidden">
                <button
                  onClick={() => setTranscriptOpen((v) => !v)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-secondary/50 transition-colors"
                >
                  {isPlaying && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                  )}
                  <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wide shrink-0">Transcript</span>
                  {!transcriptOpen && latestTranscriptLine && (
                    <span className="text-sm text-muted-foreground truncate flex-1 text-left">
                      {latestTranscriptLine.speaker === DEMO_USER ? "You" : latestTranscriptLine.speaker}: {latestTranscriptLine.text}
                    </span>
                  )}
                  {transcriptOpen && (
                    <span className="text-xs text-muted-foreground/50 font-mono tabular-nums">
                      {transcript.length}
                    </span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground/50 ml-auto shrink-0 transition-transform ${transcriptOpen ? "rotate-180" : ""}`} />
                </button>
                {transcriptOpen && (
                  <div className="border-t border-border/60 h-[180px] overflow-y-auto custom-scrollbar px-4 py-3">
                    {transcript.length > 0 ? (
                      <div className="space-y-2.5">
                        {transcript.map((seg) => {
                          const isUser = seg.speaker === DEMO_USER;
                          return (
                            <div key={seg.id} className="space-y-0.5">
                              <span className={`text-xs font-semibold ${isUser ? "text-primary/80" : "text-foreground/75"}`}>
                                {isUser ? "You" : seg.speaker}
                              </span>
                              <p className="text-sm text-foreground/70 leading-[1.6]">{seg.text}</p>
                            </div>
                          );
                        })}
                        <div ref={transcriptEndRef} />
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/40 text-center py-3">Transcript will appear here</p>
                    )}
                  </div>
                )}
              </div>

              {/* Cards section */}
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 min-h-0">
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {displayedDemoCards.map((card) => (
                      <SmartCard
                        key={card.card_id}
                        card={card}
                        onDismiss={dismissCard}
                        onPin={pinCard}
                        onUsed={markUsed}
                      />
                    ))}
                  </AnimatePresence>
                </div>

                {displayedDemoCards.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-16">
                    {isPlaying ? (
                      <>
                        <div className="relative mb-8">
                          <div className="w-20 h-20 rounded-full bg-primary/[0.04] breathe" />
                          <div className="w-14 h-14 rounded-full bg-primary/[0.06] breathe absolute top-3 left-3" style={{ animationDelay: "0.5s" }} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Zap className="w-6 h-6 text-primary/25" />
                          </div>
                        </div>
                        <p className="text-foreground/60 text-base font-medium tracking-tight">Shadow is listening</p>
                        <p className="text-muted-foreground/40 text-sm mt-2 max-w-[280px] leading-relaxed">
                          Cards will appear as the conversation unfolds
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                          <Play className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <p className="text-foreground/60 text-base font-medium mb-1">Press Start to begin</p>
                        <p className="text-muted-foreground/40 text-sm max-w-[280px] leading-relaxed">
                          Watch how Shadow surfaces real-time intelligence
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </main>

      {/* Polished completion screen */}
      <AnimatePresence>
        {meetingComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl p-6 sm:p-10 max-w-2xl w-full shadow-xl border border-border my-8"
            >
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Meeting Complete
                </h3>
                <p className="text-muted-foreground text-sm">
                  Here's what Shadow surfaced during this {Math.round(DEMO_TOTAL_DURATION / 1000)}-second demo.
                </p>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 mb-6 flex-wrap">
                {(() => {
                  const counts = {};
                  DEMO_CARDS.forEach((c) => { counts[c.card_type] = (counts[c.card_type] || 0) + 1; });
                  return Object.entries(counts).map(([type, count]) => (
                    <div key={type} className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{count}</span>
                      <span>{type.replace("_", " ")}{count > 1 ? "s" : ""}</span>
                    </div>
                  ));
                })()}
              </div>

              {/* Card gallery */}
              <div className="space-y-3 mb-8 max-h-[300px] overflow-y-auto custom-scrollbar pr-1">
                {DEMO_CARDS.map((card) => (
                  <SmartCard key={card.card_id} card={{ ...card, pinned: false, used: false }} readOnly />
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                {user ? (
                  <>
                    <button
                      onClick={() => navigate("/dashboard")}
                      className="flex-1 bg-primary text-primary-foreground py-3 text-sm rounded-lg hover:bg-primary/90 transition-all font-medium flex items-center justify-center gap-2"
                    >
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={replayDemo}
                      className="flex-1 py-3 text-sm rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Replay Demo
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleGoogleLogin}
                      className="flex-1 bg-primary text-primary-foreground py-3.5 text-sm rounded-lg hover:bg-primary/90 transition-all font-medium flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                        <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      Continue with Google
                    </button>
                    <button
                      onClick={replayDemo}
                      className="flex-1 py-3 text-sm rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Replay Demo
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
